// Totais de um fornecedor na etapa exibida.
//
// Extraído de `BudgetSupplier` para que o total do AGRUPAMENTO some exatamente
// pelo mesmo caminho que a linha do fornecedor exibe. Um segundo caminho de
// cálculo (percorrer `budgetServices`, por exemplo) daria certo hoje e
// divergiria no primeiro imposto tratado de forma especial — e "o total do
// agrupamento não bate com a soma das linhas" é bug reportado, não detalhe.

import { isTotalsIdentityVisibleInStage } from "./budget-stage-visibility";
import {
  normalizeTotalsVisibility,
  type TotalsVisibilityContext,
} from "./visible-stage-totals";

export type SupplierTotals = {
  taxes: number;
  revenueTotals: number;
  serviceTaxTotals: number;
  tariffTotals: number;
  totals: number;
};

const emptyTotals = (): SupplierTotals => ({
  taxes: 0,
  revenueTotals: 0,
  serviceTaxTotals: 0,
  tariffTotals: 0,
  totals: 0,
});

export type SupplierTotalsVisibility = TotalsVisibilityContext & {
  version: number;
};

/**
 * Soma as entradas de `totalsSource` que pertencem ao fornecedor informado.
 *
 * `totalsSource` é o nó `byCalculationType[calcOption]` da árvore de totais:
 * um objeto de arrays por tipo de item, cada entrada carregando os subtotais
 * já calculados em BRL.
 *
 * `visibility` é opcional: testes e agregações sem `budgetServices` mantêm o
 * comportamento anterior. Com serviços hidratados, leftover de categoria
 * (RSVP em C sem serviço) e célula `|dup|` sem linha na etapa saem da soma.
 */
export function sumSupplierTotals(
  totalsSource: any,
  supplierId: string,
  visibility?: SupplierTotalsVisibility
): { matchedItems: any[]; sumTotals: SupplierTotals } {
  const matchedItems: any[] = [];
  const totals = emptyTotals();

  for (const key in totalsSource) {
    const array = totalsSource[key];
    if (!Array.isArray(array)) continue;

    for (const item of array) {
      if (item.supplierId !== supplierId || !item.calcOption) continue;
      if (visibility) {
        const ctx = normalizeTotalsVisibility(visibility);
        if (
          (Array.isArray(ctx.budgetServices) || ctx.addedItemTypeList) &&
          !isTotalsIdentityVisibleInStage(
            item,
            key,
            item.calcOption,
            visibility.version,
            ctx.supplierList,
            ctx.budgetServices,
            ctx.addedItemTypeList,
            ctx.status
          )
        ) {
          continue;
        }
      }
      matchedItems.push(item);
      totals.taxes += item.taxes?.totalInBRL || 0;
      totals.revenueTotals += item.revenueTotals?.totalInBRL || 0;
      totals.serviceTaxTotals += item.serviceTaxTotals?.totalInBRL || 0;
      totals.tariffTotals += item.tariffTotals?.totalInBRL || 0;
      totals.totals += item.totals?.totalInBRL || 0;
    }
  }

  return { matchedItems, sumTotals: totals };
}

/**
 * De qual árvore de totais a etapa exibida lê: Fechamento, uma versão de
 * Negociação, ou o Orçamento base.
 */
export function resolveStageTotalsSource(params: {
  budgetValuesTotal: any;
  negociationValuesTotal: any;
  closingValuesTotal: any;
  versionInfo: { status?: string; version?: number } | undefined;
  calcOption: string;
}): any {
  const {
    budgetValuesTotal,
    negociationValuesTotal,
    closingValuesTotal,
    versionInfo,
    calcOption,
  } = params;

  if (versionInfo?.status === "APPROVED") {
    return closingValuesTotal?.byCalculationType?.[calcOption];
  }

  if (versionInfo?.status === "NEGOTIATE" && (versionInfo?.version ?? 0) > 0) {
    return negociationValuesTotal?.[(versionInfo.version as number) - 1]
      ?.byCalculationType?.[calcOption];
  }

  return budgetValuesTotal?.byCalculationType?.[calcOption];
}
